import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { CreateDropoffInfosErrors } from '../../../core/application/formValidation/createDropoffFormValidator'
import { NewDropoffInfos } from '../../../core/domain/models/NewDropoff'
import InputContainer, { InputContainerBackground } from '../inputs/InputContainer'
import TextInput from '../inputs/TextInput'
import showDetailsIcon from '../../../assets/icons/showDetailsIcon.svg'
import { useConfigurationContext } from '../../../core/application/contexts'
import { useMediaQuery } from '../../../core/application/hooks'
import { deviceSizes } from '../../../core/application/theme/breakpoints'
import { DropOffType } from '../../../core/domain/models'

export interface ISubmitDropoffInfosProps {
  infos?: NewDropoffInfos
  updateInfos: (infos: NewDropoffInfos) => void,
  errors?: CreateDropoffInfosErrors,
  shouldDisplayErrors: boolean
  displayWasteTypesSelection: () => void
}

const SubmitDropoffInfos: React.FC<ISubmitDropoffInfosProps> = ({
                                                                  infos,
                                                                  updateInfos,
                                                                  errors,
                                                                  shouldDisplayErrors,
                                                                  displayWasteTypesSelection
                                                                }) => {
  const intl = useIntl()
  const isMobile = useMediaQuery(parseInt(deviceSizes.tablet))
  const { dropoffTypeList } = useConfigurationContext()

  // Placeholders
  const selectPlaceholder = intl.formatMessage({ id: 'SubmitDropoff.infos.facility.placeholder' })
  let wasteTypesText = intl.formatMessage({ id: 'SubmitDropoff.infos.wastes.placeholder' })
  if (infos?.wasteCategories && infos.wasteCategories.length > 0) {
    const categoryNames = infos.wasteCategories.map((type) => {
      return type.name
    })
    wasteTypesText = categoryNames.join(', ')
  }

  const onUpdateFacility = (facilityName: string) => {
    const facility: DropOffType = dropoffTypeList.filter(type => {
      return type.name === facilityName
    })[0]

    updateInfos({
      ...infos,
      facility
    })
  }

  return (
    <SubmitDropoffInfosContainer>
      {
        !isMobile && <Info>{intl.formatMessage({ id: 'SubmitDropoff.infos.introduction' })}</Info>
      }
      <Title>{intl.formatMessage({ id: 'SubmitDropoff.infos.form.title' })}</Title>
      <InputContainer
        errorIntlKey={shouldDisplayErrors && errors?.name ? errors.name : undefined}
        background={InputContainerBackground.transparent}
      >
        <TextInput
          placeholder={intl.formatMessage({ id: 'SubmitDropoff.infos.name.placeholder' })}
          onChange={(value) => updateInfos({
            ...infos,
            name: value
          } as NewDropoffInfos)}
          value={infos?.name || ''}
          id='name'
        />
      </InputContainer>
      <InputContainer
        errorIntlKey={shouldDisplayErrors && errors?.facility ? errors.facility : undefined}
        background={InputContainerBackground.gray}
      >
        <Dropdown
          defaultValue={infos?.facility?.name || selectPlaceholder}
          onChange={(event) => {
            if (event.target.value === selectPlaceholder) {
              updateInfos({
                ...infos,
                facility: undefined
              })
            } else {
              onUpdateFacility(event.target.value)
            }
          }}
        >
          <option value={selectPlaceholder} key={selectPlaceholder}>{selectPlaceholder}</option>
          {
            dropoffTypeList.map((type) => {
              return (
                <option
                  value={type.name}
                  key={type.name}
                >
                  {type.name}
                </option>
              )
            })
          }
        </Dropdown>
        <DropdownIcon src={showDetailsIcon} alt='show-details' />
      </InputContainer>
      <InputContainer
        errorIntlKey={shouldDisplayErrors && errors?.wasteCategories ? errors.wasteCategories : undefined}
        background={InputContainerBackground.gray}
      >
        <WasteButtonContainer>
          <TextButton onClick={displayWasteTypesSelection}>
            {wasteTypesText}
          </TextButton>
          <DropdownIcon src={showDetailsIcon} alt='show-details' />
        </WasteButtonContainer>

      </InputContainer>
    </SubmitDropoffInfosContainer>
  )
}

const SubmitDropoffInfosContainer = styled.div`
  width: 100%;
`

const Info = styled.div`
  text-align: left;
  font-style: normal;
  font-weight: ${({ theme }) => theme.weight.light};
  font-size: ${({ theme }) => theme.size.small};
  color: ${({ theme }) => theme.color.black};
  font-family: ${({ theme }) => theme.font.default};
  margin-bottom: 14px;
`

const Title = styled.div`
  text-align: left;
  font-style: normal;
  font-family: ${({ theme }) => theme.font.semi};
  font-weight: ${({ theme }) => theme.weight.semi};
  font-size: ${({ theme }) => theme.size.big};
  color: ${({ theme }) => theme.color.black};
  margin-bottom: 2px;
`

const TextButton = styled.button`
  flex-grow: 1;
  font-style: normal;
  font-weight: ${({ theme }) => theme.weight.normal};
  font-size: ${({ theme }) => theme.size.small};
  color: ${({ theme }) => theme.color.black};
  line-height: 18px;
  background: transparent;
  text-align: left;
  z-index: 2;
  cursor: pointer;
  padding-right: 22px;
`

const WasteButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  cursor: pointer;
`

const Dropdown = styled.select`
  appearance: none;
  outline: none;
  border: none;
  width: 100%;
  font-style: normal;
  font-weight: ${({ theme }) => theme.weight.normal};
  font-size: ${({ theme }) => theme.size.small};
  color: ${({ theme }) => theme.color.black};
  line-height: 18px;
  background: transparent;
  text-align: left;
  z-index: 2;
  cursor: pointer;
`

const DropdownIcon = styled.img`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
`

export default SubmitDropoffInfos
